/**
 * Dispute rate per practice — the screen §8 of the design document calls "required scope,
 * not optional hardening", and which had no UI at all.
 *
 * *"Alert the superadmin when any tenant crosses a 0.5% dispute rate. Stripe's own threshold
 * is 0.75%, at which point the platform account is at risk — one bad practitioner can
 * jeopardise the whole business."*
 *
 * Both numbers are stated on screen, because a percentage with no threshold beside it is not
 * actionable: 0.6% means nothing until you know where the cliff is.
 *
 * `disputePct: null` is a REAL and alarming shape, not a divide-by-zero: it means the
 * practice has disputes but no captured payments inside the window, so all of its volume is
 * older than the window. It renders as an em dash with that explanation — never as 0.00%,
 * which would read as the safest possible practice.
 */
import React, { useCallback } from "react";

import { ALARM_ROW, ALARM_TEXT, COLHEAD, Criteria, IDENT, MUTED, RULE } from "./Ledger";
import { useLanguage } from "../../lib/LanguageContext";
import { formatPercent } from "../../lib/formatNumber";
import { Freshness, ResourceState, useGatewayResource } from "./ResourceState";
import { STRIPE_DISPUTE_CLIFF_PCT } from "./thresholds";

export default function DisputeRatesScreen({ t, gatewayApi }) {
  const { lang } = useLanguage();
  const fetcher = useCallback(() => gatewayApi.adminDisputeRates(90), [gatewayApi]);
  const resource = useGatewayResource(fetcher);
  const alertPct = Number(resource.data?.alertPct ?? 0.5);

  return (
    <div data-testid="dispute-rates-screen">
      <div className="flex items-baseline justify-between gap-4 mb-3">
        <Criteria>
          {t("gw.console.disputeRates.criteria", {
            alert: formatPercent(alertPct, lang), cliff: formatPercent(STRIPE_DISPUTE_CLIFF_PCT, lang) })}
        </Criteria>
        <Freshness resource={resource} id="dispute-rates" />
      </div>

      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr className={RULE}>
            <th className={`${COLHEAD} py-2`}>{t("gw.console.disputeRates.colTenant")}</th>
            <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.disputeRates.colCaptured")}</th>
            <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.disputeRates.colDisputed")}</th>
            <th className={`${COLHEAD} py-2 text-right`}>{t("gw.console.disputeRates.colRate")}</th>
            <th className={`${COLHEAD} py-2`}>{t("gw.console.disputeRates.colVerdict")}</th>
          </tr>
        </thead>
        <ResourceState
          resource={resource} id="dispute-rates" as="tbody" colSpan={5}
          emptyText={t("gw.console.disputeRates.empty")}
          isEmpty={(d) => (d?.rows || []).length === 0}>
          {(data) => (data.rows || []).map((r) => {
            const pct = r.disputePct == null ? null : Number(r.disputePct);
            const overCliff = pct != null && pct >= STRIPE_DISPUTE_CLIFF_PCT;
            const overAlert = pct != null && pct >= alertPct;
            const unknown = pct == null;
            return (
              <tr key={`${r.tenantId}`} className={RULE}
                  data-testid={`dispute-rate-row-${r.tenantId}`}>
                <td className={`py-2 ${IDENT}`}>{r.tenantId}</td>
                <td className="py-2 text-right text-slate-700">{r.capturedCount ?? 0}</td>
                <td className="py-2 text-right text-slate-700">{r.disputeCount ?? 0}</td>
                <td className={`py-2 text-right ${overAlert ? ALARM_TEXT : "text-slate-900"}`}
                    data-testid={`dispute-rate-pct-${r.tenantId}`}>
                  {unknown ? <span className="text-slate-300">—</span> : formatPercent(pct, lang)}
                </td>
                <td className="py-2">
                  {unknown && (
                    <span className={MUTED} data-testid={`dispute-rate-unknown-${r.tenantId}`}>
                      {t("gw.console.disputeRates.noVolume")}
                    </span>
                  )}
                  {overCliff && (
                    <span className={`${ALARM_ROW} text-xs block`} role="alert"
                          data-testid={`dispute-rate-cliff-${r.tenantId}`}>
                      {t("gw.console.disputeRates.overCliff", {
                        cliff: formatPercent(STRIPE_DISPUTE_CLIFF_PCT, lang) })}
                    </span>
                  )}
                  {!overCliff && overAlert && (
                    <span className={`${ALARM_ROW} text-xs block`} role="alert"
                          data-testid={`dispute-rate-alert-${r.tenantId}`}>
                      {t("gw.console.disputeRates.overAlert", { alert: formatPercent(alertPct, lang) })}
                    </span>
                  )}
                  {!overAlert && !unknown && (
                    <span className={MUTED}>{t("gw.console.disputeRates.ok")}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </ResourceState>
      </table>
    </div>
  );
}
